import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../components/content_wrapper/header";
import CustomLoader from "../../components/customLoader";
import NoRecordFound from "../../components/noRecordFound";
import Pagination from "../../utils/pagination";
import {
  useGetErrorLogs,
  ErrorLogFilters,
} from "../../hooks/errorLogs/useGetErrorLogs";
import { useResolveErrorLog } from "../../hooks/errorLogs/useResolveErrorLog";
import { useDeleteErrorLog } from "../../hooks/errorLogs/useDeleteErrorLog";
import {
  useGetCollectionCodeAuditLogs,
  CollectionCodeAuditFilters,
} from "../../hooks/errorLogs/useGetCollectionCodeAuditLogs";
import {
  useGetAuthAuditLogs,
  AuthAuditFilters,
} from "../../hooks/errorLogs/useGetAuthAuditLogs";
import { useGetAuthAuditStats } from "../../hooks/errorLogs/useGetAuthAuditStats";
import { useGetAuthAuditLogDetail } from "../../hooks/errorLogs/useGetAuthAuditLogDetail";
import {
  useGetAppCheckLogs,
  AppCheckLogFilters,
} from "../../hooks/errorLogs/useGetAppCheckLogs";
import {
  useGetFlaggedEmails,
  FlaggedEmailFilters,
} from "../../hooks/errorLogs/useGetFlaggedEmails";
import { useResolveFlaggedEmail } from "../../hooks/errorLogs/useResolveFlaggedEmail";
import { useDeleteFlaggedEmail } from "../../hooks/errorLogs/useDeleteFlaggedEmail";

// ─── Domain colour badges ────────────────────────────────────────────────────
const DOMAIN_COLOURS: Record<string, string> = {
  AUTH: "#e74c3c",
  KEY: "#2980b9",
  OWNER: "#8e44ad",
  BOX: "#16a085",
  KEY_HOST: "#d35400",
  COLLECTION_CODE: "#c0392b",
  SUBSCRIPTION: "#27ae60",
  COUPON: "#f39c12",
  ADMIN: "#7f8c8d",
  PAYMENT: "#1abc9c",
  CRON: "#2c3e50",
  GENERAL: "#95a5a6",
};

function DomainBadge({ domain }: { domain: string }) {
  const colour = DOMAIN_COLOURS[domain] || "#95a5a6";
  return (
    <span
      style={{
        backgroundColor: colour,
        color: "#fff",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.5,
        whiteSpace: "nowrap",
      }}
    >
      {domain}
    </span>
  );
}

// ─── Detail Modal ────────────────────────────────────────────────────────────
interface DetailModalProps {
  log: any;
  onClose: () => void;
  onResolve: (id: string) => void;
  resolving: boolean;
}

function DetailModal({ log, onClose, onResolve, resolving }: DetailModalProps) {
  return (
    <div
      className="modal show"
      style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fa-solid fa-triangle-exclamation me-2 text-warning" />
              Error Detail
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <table className="table table-bordered table-sm">
              <tbody>
                <tr>
                  <th style={{ width: 160 }}>Reference ID</th>
                  <td>
                    <code style={{ fontSize: 12 }}>{log.requestId}</code>
                  </td>
                </tr>
                <tr>
                  <th>Domain</th>
                  <td>
                    <DomainBadge domain={log.domain} />
                  </td>
                </tr>
                <tr>
                  <th>Error Code</th>
                  <td>
                    <code>{log.errorCode}</code>
                  </td>
                </tr>
                <tr>
                  <th>HTTP Status</th>
                  <td>
                    <span
                      className={`badge bg-${
                        log.statusCode >= 500 ? "danger" : "warning"
                      } text-white`}
                    >
                      {log.statusCode}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Message</th>
                  <td>{log.message}</td>
                </tr>
                <tr>
                  <th>Endpoint</th>
                  <td>
                    <code>
                      {log.method} {log.path}
                    </code>
                  </td>
                </tr>
                <tr>
                  <th>User</th>
                  <td>
                    {log.userId ? `${log.userRole} — ${log.userId}` : "—"}
                  </td>
                </tr>
                <tr>
                  <th>IP</th>
                  <td>{log.ip || "—"}</td>
                </tr>
                <tr>
                  <th>Timestamp</th>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <th>Expires At</th>
                  <td>{new Date(log.expiresAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <th>Resolved</th>
                  <td>
                    {log.resolved
                      ? `Yes — ${new Date(log.resolvedAt).toLocaleString()}`
                      : "No"}
                  </td>
                </tr>
                {log.stack && (
                  <tr>
                    <th>Stack Trace</th>
                    <td>
                      <pre
                        style={{
                          fontSize: 11,
                          maxHeight: 200,
                          overflowY: "auto",
                          background: "#f8f9fa",
                          padding: 8,
                          borderRadius: 4,
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                        }}
                      >
                        {log.stack}
                      </pre>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="modal-footer">
            {!log.resolved && (
              <button
                className="btn btn-success"
                onClick={() => onResolve(log._id)}
                disabled={resolving}
              >
                {resolving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Resolving…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check me-1" />
                    Mark as Resolved
                  </>
                )}
              </button>
            )}
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Event Type Badge ────────────────────────────────────────────────────────
const EVENT_TYPE_COLOURS: Record<string, string> = {
  // camelCase (after admin response transformer)
  validationFailed: "#e74c3c",
  validationSuccess: "#27ae60",
  used: "#27ae60",
  created: "#2980b9",
  expired: "#f39c12",
  revoked: "#8e44ad",
  modified: "#16a085",
  // snake_case fallbacks (in case transformer skips enum strings)
  validation_failed: "#e74c3c",
  validation_success: "#27ae60",
};

function EventTypeBadge({ type }: { type: string }) {
  const colour = EVENT_TYPE_COLOURS[type] || "#95a5a6";
  // Normalise display: camelCase → words, snake_case → words
  const label = type
    ?.replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return (
    <span
      style={{
        backgroundColor: colour,
        color: "#fff",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── Auth Audit Event Type colours ───────────────────────────────────────────
const AUTH_EVENT_COLOURS: Record<string, string> = {
  LOGIN_FAILED:   "#e74c3c",
  LOGIN_SUCCESS:  "#27ae60",
  OTP_FAILED:     "#e67e22",
  OTP_SUCCESS:    "#16a085",
  LOGOUT:         "#7f8c8d",
};

function AuthEventBadge({ type }: { type: string }) {
  const colour = AUTH_EVENT_COLOURS[type] || "#95a5a6";
  return (
    <span
      style={{
        backgroundColor: colour,
        color: "#fff",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {type?.replace(/_/g, " ")}
    </span>
  );
}

// ─── Auth Role Badge ──────────────────────────────────────────────────────────
const ROLE_COLOURS: Record<string, string> = {
  OWNER:   "#8e44ad",
  HOST:    "#d35400",
  ADMIN:   "#7f8c8d",
  UNKNOWN: "#bdc3c7",
};

function RoleBadge({ role }: { role?: string }) {
  if (!role) return <span style={{ color: "#aaa" }}>—</span>;
  const colour = ROLE_COLOURS[role] || "#95a5a6";
  return (
    <span
      style={{
        backgroundColor: colour,
        color: "#fff",
        padding: "2px 7px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {role}
    </span>
  );
}

// ─── Logon Audit Detail Modal ─────────────────────────────────────────────────
interface LogonAuditDetailModalProps {
  log: any;
  onClose: () => void;
}

function LogonAuditDetailModal({ log, onClose }: LogonAuditDetailModalProps) {
  const [revealRequested, setRevealRequested] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const isSuccess = log.eventType === "LOGIN_SUCCESS";

  const {
    data: detailData,
    isFetching: fetchingDetail,
  } = useGetAuthAuditLogDetail(
    confirmed ? log._id : null,
    true
  );

  const revealedPassword =
    detailData?.data?.attemptedPassword ?? detailData?.attemptedPassword ?? null;

  const handleRevealClick = () => {
    if (!confirmed) {
      setRevealRequested(true);
    }
  };

  const handleConfirmReveal = () => {
    setConfirmed(true);
    setRevealRequested(false);
  };

  const metaEntries = log.metadata
    ? Object.entries(log.metadata as Record<string, any>)
    : [];

  return (
    <div
      className="modal show"
      style={{ display: "block", background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2">
              <i
                className={`fa-solid ${
                  isSuccess
                    ? "fa-circle-check text-success"
                    : "fa-circle-xmark text-danger"
                } me-1`}
              />
              Logon Audit Detail
              <AuthEventBadge type={log.eventType} />
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body" style={{ fontSize: 13 }}>

            {/* ── Identity ── */}
            <h6 className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
              Identity
            </h6>
            <table className="table table-bordered table-sm mb-4">
              <tbody>
                <tr>
                  <th style={{ width: 180 }}>Email</th>
                  <td>{log.userEmail || "—"}</td>
                </tr>
                <tr>
                  <th>Login Identifier</th>
                  <td><code style={{ fontSize: 12 }}>{log.loginIdentifier || "—"}</code></td>
                </tr>
                <tr>
                  <th>User ID</th>
                  <td>
                    {log.userId
                      ? <code style={{ fontSize: 12 }}>{log.userId}</code>
                      : <span className="text-muted">not resolved</span>}
                  </td>
                </tr>
                <tr>
                  <th>Role</th>
                  <td><RoleBadge role={log.userRole} /></td>
                </tr>
                <tr>
                  <th>Auth Method</th>
                  <td>{log.authMethod || "—"}</td>
                </tr>
              </tbody>
            </table>

            {/* ── Event ── */}
            <h6 className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
              Event
            </h6>
            <table className="table table-bordered table-sm mb-4">
              <tbody>
                <tr>
                  <th style={{ width: 180 }}>Event Type</th>
                  <td><AuthEventBadge type={log.eventType} /></td>
                </tr>
                <tr>
                  <th>Failure Reason</th>
                  <td>
                    {log.failureReason
                      ? <span className="text-danger fw-semibold">{log.failureReason}</span>
                      : <span className="text-muted">—</span>}
                  </td>
                </tr>
                <tr>
                  <th>Error Details</th>
                  <td>{log.errorDetails ? JSON.stringify(log.errorDetails) : <span className="text-muted">—</span>}</td>
                </tr>
                <tr>
                  <th>Token Generated</th>
                  <td>
                    {log.tokenGenerated === true
                      ? <span className="badge bg-success">Yes</span>
                      : <span className="badge bg-secondary">No</span>}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ── Session (success only) ── */}
            {isSuccess && (
              <>
                <h6 className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                  Session
                </h6>
                <table className="table table-bordered table-sm mb-4">
                  <tbody>
                    <tr>
                      <th style={{ width: 180 }}>Session Token</th>
                      <td>
                        {log.sessionToken
                          ? (
                            <code
                              style={{
                                fontSize: 11,
                                wordBreak: "break-all",
                                display: "block",
                                background: "#f0fdf4",
                                padding: "4px 6px",
                                borderRadius: 4,
                              }}
                            >
                              {log.sessionToken}
                            </code>
                          )
                          : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* ── Request ── */}
            <h6 className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
              Request
            </h6>
            <table className="table table-bordered table-sm mb-4">
              <tbody>
                <tr>
                  <th style={{ width: 180 }}>Request ID</th>
                  <td><code style={{ fontSize: 11 }}>{log.requestId || "—"}</code></td>
                </tr>
                <tr>
                  <th>IP Address</th>
                  <td><code style={{ fontSize: 12 }}>{log.ipAddress || "—"}</code></td>
                </tr>
                <tr>
                  <th>Forwarded For</th>
                  <td><code style={{ fontSize: 12 }}>{log.forwardedFor || "—"}</code></td>
                </tr>
                <tr>
                  <th>Origin / Referer</th>
                  <td>{log.origin || "—"}</td>
                </tr>
                <tr>
                  <th>User Agent</th>
                  <td>
                    <span style={{ fontSize: 12, wordBreak: "break-all" }}>
                      {log.userAgent || "—"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ── Metadata ── */}
            {metaEntries.length > 0 && (
              <>
                <h6 className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                  Metadata
                </h6>
                <table className="table table-bordered table-sm mb-4">
                  <tbody>
                    {metaEntries.map(([key, val]) => (
                      <tr key={key}>
                        <th style={{ width: 180 }}>
                          {key
                            .replace(/_/g, " ")
                            .replace(/([a-z])([A-Z])/g, "$1 $2")
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </th>
                        <td>
                          <code style={{ fontSize: 12 }}>
                            {typeof val === "object"
                              ? JSON.stringify(val)
                              : String(val ?? "—")}
                          </code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* ── Timestamps ── */}
            <h6 className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
              Timestamps
            </h6>
            <table className="table table-bordered table-sm mb-4">
              <tbody>
                <tr>
                  <th style={{ width: 180 }}>Event Timestamp</th>
                  <td>{log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}</td>
                </tr>
                <tr>
                  <th>Created At</th>
                  <td>{log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}</td>
                </tr>
              </tbody>
            </table>

            {/* ── Attempted Password (failures only) ── */}
            {!isSuccess && (
              <>
                <h6 className="fw-semibold text-muted mb-2 d-flex align-items-center gap-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                  Attempted Password
                  <span className="badge bg-danger" style={{ fontSize: 10, fontWeight: 700 }}>
                    SENSITIVE
                  </span>
                </h6>
                <div
                  className="p-3 mb-3"
                  style={{
                    background: "#fff5f5",
                    border: "1px solid #f5c6cb",
                    borderRadius: 6,
                  }}
                >
                  {!confirmed && !revealRequested && (
                    <div className="d-flex align-items-center gap-3">
                      <span style={{ fontSize: 12, color: "#7f0000" }}>
                        <i className="fa-solid fa-lock me-1" />
                        The raw attempted password is hidden. This is sensitive
                        audit data and should only be accessed for legitimate
                        support investigations.
                      </span>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={handleRevealClick}
                      >
                        <i className="fa-solid fa-eye me-1" />
                        Reveal Password
                      </button>
                    </div>
                  )}

                  {revealRequested && !confirmed && (
                    <div>
                      <p className="text-danger fw-semibold mb-2" style={{ fontSize: 12 }}>
                        <i className="fa-solid fa-triangle-exclamation me-1" />
                        Are you sure? Revealing passwords should only be done for
                        legitimate support purposes. This action is not logged
                        separately.
                      </p>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleConfirmReveal}
                        >
                          Yes, reveal it
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setRevealRequested(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {confirmed && (
                    <div>
                      {fetchingDetail ? (
                        <span className="spinner-border spinner-border-sm text-danger" />
                      ) : revealedPassword != null ? (
                        <div>
                          <p className="text-danger fw-semibold mb-1" style={{ fontSize: 11 }}>
                            <i className="fa-solid fa-triangle-exclamation me-1" />
                            Raw attempted password — handle with care:
                          </p>
                          <code
                            style={{
                              display: "block",
                              background: "#2d0000",
                              color: "#ff6b6b",
                              padding: "8px 12px",
                              borderRadius: 4,
                              fontSize: 13,
                              wordBreak: "break-all",
                              letterSpacing: 0.5,
                            }}
                          >
                            {revealedPassword}
                          </code>
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          No attempted password recorded for this event.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Screen ─────────────────────────────────────────────────────────────
const DOMAINS = [
  "AUTH",
  "KEY",
  "OWNER",
  "BOX",
  "KEY_HOST",
  "COLLECTION_CODE",
  "SUBSCRIPTION",
  "COUPON",
  "ADMIN",
  "PAYMENT",
  "CRON",
  "GENERAL",
];
const EVENT_TYPES = [
  "validation_failed",
  "used",
  "created",
  "expired",
  "revoked",
  "modified",
];

// ─── Operation constants ──────────────────────────────────────────────────────
const OPERATIONS: { value: string; label: string }[] = [
  // Key ingestion (entering store)
  { value: "registration",          label: "Registration" },
  { value: "keyring_drop_off",      label: "Key Drop-Off" },
  { value: "placement_confirmation",label: "Placement Confirmation" },
  // Key collection (leaving store)
  { value: "collection",            label: "Collection Code" },
  { value: "owner",                 label: "Owner Collection Code" },
  { value: "owner_return_code",     label: "Owner Return Code" },
  // Legacy
  { value: "keyring_scan",          label: "Keyring Scan (Legacy)" },
  { value: "keyring_link",          label: "Keyring Link (Legacy)" },
];

const OPERATION_COLOURS: Record<string, string> = {
  // Ingestion — blues/greens
  registration:           "#1a6b9a",
  keyring_drop_off:       "#0e7c61",
  placement_confirmation: "#2e86ab",
  // Collection — purples/warm
  collection:             "#7d3c98",
  owner:                  "#6c3483",
  owner_return_code:      "#922b21",
  // Legacy — neutral
  keyring_scan:           "#7f8c8d",
  keyring_link:           "#626567",
};

function OperationBadge({ operation }: { operation?: string }) {
  if (!operation) return <span style={{ color: "#aaa" }}>—</span>;
  const colour = OPERATION_COLOURS[operation] || "#95a5a6";
  const label =
    OPERATIONS.find((o) => o.value === operation)?.label ||
    operation.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      style={{
        backgroundColor: colour,
        color: "#fff",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── Code Audit Detail Modal ─────────────────────────────────────────────────

const KEY_STATUS_NAMES: Record<number, string> = {
  0: "Awaiting registration",
  1: "In store (available)",
  2: "In use (with owner)",
  3: "Deleted",
  4: "Returned to owner",
};

const METADATA_LABELS: Record<string, string> = {
  keyStatus:             "Key Status at Attempt",
  currentStoreId:        "Key's Current Store",
  attemptedStoreId:      "Store Attempted",
  subscriptionExpiresAt: "Subscription Expired At",
  existingKeyId:         "Conflicting Key ID",
  existingKeyName:       "Conflicting Key Name",
  boxesChecked:          "Active Boxes Checked",
  keyName:               "Key Name",
  boxId:                 "Box ID",
  slotNumber:            "Slot Number",
  subscriptionStatus:    "Subscription Active",
  keyFound:              "Key Found",
  boxFound:              "Box Found",
  qrcode:                "QR Code Scanned",
  operationType:         "Operation Type",
  previousStatus:        "Previous Status",
  newStatus:             "New Status",
  reservedSlotId:        "Reserved Slot ID",
  keyringCodeProvided:   "Keyring Code Provided",
  expectedHostId:        "Authorized Host ID",
  expectedHostName:      "Authorized Host Name",
};

function resolveFailureReason(errorCode: string, failureReason: string): string {
  // Override stored failure_reason for codes where the old message was ambiguous.
  // Keeps new records consistent with pre-fix records.
  const canonical: Record<string, string> = {
    INVALID_REGISTRATION_CODE:      "Registration code does not exist",
    REGISTRATION_CODE_ALREADY_USED: "Registration code has already been used",
  };
  return canonical[errorCode] || failureReason || "—";
}

function getDiagnosticTip(errorCode: string, metadata: any): string {
  switch (errorCode) {
    case "INVALID_KEYRING_CODE":
      return "The keyring code was not found in the system. Check that the physical label on the keyring matches its registered code, or that this key belongs to this store.";
    case "INVALID_CODE":
      return "The collection code was not found. It may have expired, already been used, or been entered incorrectly.";
    case "INVALID_OWNER_RETURN_CODE":
      return "The owner return code did not match any key. The physical keyring QR may be damaged, belong to a different store, or the key may have been removed.";
    case "INVALID_REGISTRATION_CODE":
      return "The registration code does not exist. Check the code in the owner's welcome pack — it may have been entered incorrectly.";
    case "REGISTRATION_CODE_ALREADY_USED":
      return "This registration code has already been used. Each registration code is single-use — the key has already been registered.";
    case "KEY_NOT_IN_USE": {
      const s = metadata?.keyStatus ?? metadata?.key_status;
      const name = s !== undefined ? (KEY_STATUS_NAMES[s] ?? `status ${s}`) : "an unexpected status";
      return `The key was "${name}" at the time. For a drop-off, the key must be in-use (held by the owner).`;
    }
    case "KEY_ALREADY_AT_STORE":
      return "The key is already registered as being at a store. It cannot be dropped off again until it has been collected by the owner.";
    case "KEY_NOT_AT_STORE":
      return "The key exists but is registered at a different store. The owner may have gone to the wrong location.";
    case "KEY_NOT_AVAILABLE":
      return "The key was found but its current status does not permit pickup. Check the key record for its current state.";
    case "SUBSCRIPTION_EXPIRED": {
      const ts = metadata?.subscriptionExpiresAt ?? metadata?.subscription_expires_at;
      const dateStr = ts ? new Date(ts * 1000).toLocaleString() : null;
      return `The key subscription expired${dateStr ? ` on ${dateStr}` : ""}. The owner must renew before this key can be stored.`;
    }
    case "NO_SLOTS_AVAILABLE":
      return "No free slots were found across all boxes at this store. The key host may need to open additional capacity.";
    case "KEYRING_CODE_ALREADY_IN_USE":
      return "This keyring code is already assigned to another key. Check for duplicate physical keyrings or a data entry error.";
    case "KEYRING_CODE_NOT_FOR_COLLECTION":
      return "A keyring code was used where a collection code was expected. The person scanned the wrong side of the keyring.";
    case "KEY_SLOT_NOT_ASSIGNED":
      return "The key is registered at this store but has no slot assigned — this is a data integrity issue. Contact support with the key ID.";
    case "RATE_LIMIT_EXCEEDED":
      return "Too many attempts from this IP address in a short period. May indicate a retry loop in the mobile app or a brute-force attempt.";
    case "KEY_HOST_UNAUTHORIZED_STORE": {
      const hostName = metadata?.expectedHostName ?? metadata?.expected_host_name;
      return `This key is authorized for a different store${hostName ? ` (${hostName})` : ""}. The owner must change the authorized host, or bring the key to the correct store.`;
    }
    case "BOX_AT_CAPACITY":
      return "The authorized host has no available slots right now. The owner should choose a different host, or the host needs additional capacity.";
    default:
      return "";
  }
}

function formatMetaValue(key: string, value: any): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if ((key === "keyStatus" || key === "key_status") && typeof value === "number") {
    return `${value} — ${KEY_STATUS_NAMES[value] ?? "Unknown"}`;
  }
  if ((key === "previousStatus" || key === "newStatus") && typeof value === "number") {
    return `${value} — ${KEY_STATUS_NAMES[value] ?? "Unknown"}`;
  }
  if ((key === "subscriptionExpiresAt" || key === "subscription_expires_at") && typeof value === "number") {
    return new Date(value * 1000).toLocaleString();
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

interface CodeAuditDetailModalProps {
  log: any;
  onClose: () => void;
}

function CodeAuditDetailModal({ log, onClose }: CodeAuditDetailModalProps) {
  const tip = log.errorCode ? getDiagnosticTip(log.errorCode, log.metadata) : "";
  const metaEntries = log.metadata
    ? Object.entries(log.metadata as Record<string, any>).filter(
        ([, v]) => v !== null && v !== undefined && v !== ""
      )
    : [];

  return (
    <div
      className="modal show"
      style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2">
              <i
                className={`fa-solid ${
                  log.success
                    ? "fa-circle-check text-success"
                    : "fa-circle-xmark text-danger"
                } me-1`}
              />
              Code Audit Detail
              <OperationBadge operation={log.operation} />
              <EventTypeBadge type={log.eventType} />
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body" style={{ fontSize: 13 }}>

            {/* Diagnostic tip */}
            {!log.success && tip && (
              <div
                className="alert alert-warning d-flex gap-2 mb-3 py-2"
                style={{ fontSize: 12 }}
              >
                <i className="fa-solid fa-lightbulb mt-1 flex-shrink-0" />
                <span>{tip}</span>
              </div>
            )}

            {/* Code & Key */}
            <h6
              className="fw-semibold text-muted mb-2"
              style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}
            >
              Code &amp; Key
            </h6>
            <table className="table table-bordered table-sm mb-3">
              <tbody>
                <tr>
                  <th style={{ width: 165 }}>Code Used</th>
                  <td>
                    {log.code ? (
                      <code
                        style={{
                          fontSize: 13,
                          color: log.success ? "#27ae60" : "#c0392b",
                          fontWeight: 700,
                        }}
                      >
                        {log.code}
                      </code>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Key</th>
                  <td>
                    {log.keyName ? (
                      <span style={{ fontWeight: 500 }}>{log.keyName}</span>
                    ) : log.keyId ? (
                      <code style={{ fontSize: 11 }}>{log.keyId}</code>
                    ) : (
                      <span className="text-muted">not resolved</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Result</th>
                  <td>
                    {log.success ? (
                      <span className="badge bg-success">Success</span>
                    ) : (
                      <span className="badge bg-danger">Failed</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Timestamp</th>
                  <td>
                    {log.eventTimestamp
                      ? new Date(log.eventTimestamp * 1000).toLocaleString()
                      : log.createdAt
                      ? new Date(log.createdAt).toLocaleString()
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Failure details */}
            {!log.success && (
              <>
                <h6
                  className="fw-semibold text-muted mb-2"
                  style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}
                >
                  Failure Details
                </h6>
                <table className="table table-bordered table-sm mb-3">
                  <tbody>
                    <tr>
                      <th style={{ width: 165 }}>Error Code</th>
                      <td>
                        <code style={{ fontSize: 12, color: "#c0392b" }}>
                          {log.errorCode || "—"}
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <th>Failure Reason</th>
                      <td style={{ color: "#c0392b" }}>{resolveFailureReason(log.errorCode, log.failureReason)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* Actor & Store */}
            <h6
              className="fw-semibold text-muted mb-2"
              style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}
            >
              Actor &amp; Store
            </h6>
            <table className="table table-bordered table-sm mb-3">
              <tbody>
                <tr>
                  <th style={{ width: 165 }}>Actor</th>
                  <td>
                    {log.actorName || "—"}
                    {log.actorType && (
                      <span
                        className="badge bg-secondary ms-2"
                        style={{ fontSize: 10 }}
                      >
                        {log.actorType.replace(/_/g, " ")}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Store</th>
                  <td>
                    {log.storeName || (
                      log.storeId
                        ? <code style={{ fontSize: 11 }}>{log.storeId}</code>
                        : <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>IP Address</th>
                  <td>
                    <code style={{ fontSize: 12 }}>{log.ipAddress || "—"}</code>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Metadata / Context */}
            {metaEntries.length > 0 && (
              <>
                <h6
                  className="fw-semibold text-muted mb-2"
                  style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}
                >
                  Context
                </h6>
                <table className="table table-bordered table-sm mb-3">
                  <tbody>
                    {metaEntries.map(([key, val]) => (
                      <tr key={key}>
                        <th style={{ width: 165 }}>
                          {METADATA_LABELS[key] ||
                            key
                              .replace(/_/g, " ")
                              .replace(/([a-z])([A-Z])/g, "$1 $2")
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </th>
                        <td>
                          <code style={{ fontSize: 12 }}>
                            {formatMetaValue(key, val)}
                          </code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Request */}
            <h6
              className="fw-semibold text-muted mb-2"
              style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}
            >
              Request
            </h6>
            <table className="table table-bordered table-sm mb-1">
              <tbody>
                <tr>
                  <th style={{ width: 165 }}>Reference ID</th>
                  <td>
                    <code style={{ fontSize: 11, wordBreak: "break-all" }}>
                      {log.requestId || "—"}
                    </code>
                  </td>
                </tr>
                <tr>
                  <th>User Agent</th>
                  <td>
                    <span style={{ fontSize: 11, wordBreak: "break-all" }}>
                      {log.userAgent || "—"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Failure Digest ───────────────────────────────────────────────────────────

const ERROR_CODE_COLOUR: Record<string, string> = {
  INVALID_KEYRING_CODE:          "#c0392b",
  INVALID_CODE:                  "#c0392b",
  INVALID_OWNER_RETURN_CODE:     "#c0392b",
  INVALID_REGISTRATION_CODE:          "#c0392b",
  REGISTRATION_CODE_ALREADY_USED:     "#8e44ad",
  KEY_NOT_IN_USE:                "#e67e22",
  KEY_ALREADY_AT_STORE:          "#e67e22",
  KEY_NOT_AT_STORE:              "#e67e22",
  KEY_NOT_AVAILABLE:             "#e67e22",
  SUBSCRIPTION_EXPIRED:          "#8e44ad",
  NO_SLOTS_AVAILABLE:            "#2980b9",
  KEYRING_CODE_ALREADY_IN_USE:   "#16a085",
  KEYRING_CODE_NOT_FOR_COLLECTION: "#7f8c8d",
  KEY_SLOT_NOT_ASSIGNED:         "#7f0000",
  RATE_LIMIT_EXCEEDED:           "#7f8c8d",
};

function SpikeChart({ logs }: { logs: any[] }) {
  const failures = logs.filter((l) => !l.success);
  if (failures.length < 3) return null;

  const buckets: Record<string, number> = {};
  failures.forEach((l) => {
    const ts = l.eventTimestamp ? l.eventTimestamp * 1000 : Date.parse(l.createdAt);
    if (!ts) return;
    const d = new Date(ts);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:00`;
    buckets[key] = (buckets[key] || 0) + 1;
  });

  const sorted = Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b));
  if (sorted.length < 2) return null;

  const max = Math.max(...sorted.map(([, v]) => v));
  const SPIKE_THRESHOLD = Math.ceil(max * 0.7);

  return (
    <div className="card mb-3">
      <div className="card-body py-3">
        <p className="mb-2 fw-semibold" style={{ fontSize: 13 }}>
          <i className="fa-solid fa-chart-simple me-2 text-danger" />
          Failure Distribution by Hour
        </p>
        <div className="d-flex align-items-flex-end gap-1" style={{ height: 48, overflowX: "auto" }}>
          {sorted.map(([label, count]) => {
            const pct = max > 0 ? Math.round((count / max) * 100) : 0;
            const isSpike = count >= SPIKE_THRESHOLD;
            return (
              <div key={label} title={`${label}: ${count} failure${count !== 1 ? "s" : ""}`}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "0 0 auto", minWidth: 28 }}>
                <div style={{
                  width: 20,
                  height: `${pct}%`,
                  minHeight: 4,
                  background: isSpike ? "#e74c3c" : "#72bcb8",
                  borderRadius: 3,
                  transition: "height 0.2s",
                  alignSelf: "flex-end",
                }} />
                <span style={{ fontSize: 9, color: "#888", marginTop: 2, whiteSpace: "nowrap" }}>
                  {label.slice(11, 16)}
                </span>
              </div>
            );
          })}
        </div>
        {sorted.some(([, v]) => v >= SPIKE_THRESHOLD) && (
          <p className="mb-0 mt-2 text-danger" style={{ fontSize: 12 }}>
            <i className="fa-solid fa-triangle-exclamation me-1" />
            Spike detected — red bars show hours with unusually high failure volume
          </p>
        )}
      </div>
    </div>
  );
}

function FailureDigest({ logs }: { logs: any[] }) {
  const failures = logs.filter((l) => !l.success);
  if (failures.length === 0) return null;

  // Top error codes
  const byError: Record<string, number> = {};
  failures.forEach((l) => {
    const k = l.errorCode || "UNKNOWN";
    byError[k] = (byError[k] || 0) + 1;
  });
  const topErrors = Object.entries(byError).sort(([, a], [, b]) => b - a);

  // Top failing stores
  const byStore: Record<string, number> = {};
  failures.forEach((l) => {
    if (!l.storeName) return;
    byStore[l.storeName] = (byStore[l.storeName] || 0) + 1;
  });
  const topStores = Object.entries(byStore)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  // Repeat codes — only surface codes failing more than once
  const byCode: Record<string, number> = {};
  failures.forEach((l) => {
    if (!l.code || l.code === "***") return;
    byCode[l.code] = (byCode[l.code] || 0) + 1;
  });
  const repeatCodes = Object.entries(byCode)
    .filter(([, c]) => c > 1)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 4);

  return (
    <div
      className="card mb-3"
      style={{ borderLeft: "4px solid #e74c3c", background: "#fff8f8" }}
    >
      <div className="card-body py-2 px-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <i
            className="fa-solid fa-triangle-exclamation text-danger"
            style={{ fontSize: 13 }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#7f0000",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            Failure Digest —{" "}
            <strong>{failures.length}</strong> failure
            {failures.length !== 1 ? "s" : ""} in current view
          </span>
        </div>

        <div className="row g-2">
          {/* Error code breakdown */}
          <div className="col-md-5">
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#aaa",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 5,
              }}
            >
              By Error Code
            </div>
            <div className="d-flex flex-wrap gap-1">
              {topErrors.map(([code, count]) => (
                <span
                  key={code}
                  style={{
                    background: ERROR_CODE_COLOUR[code] || "#7f8c8d",
                    color: "#fff",
                    borderRadius: 4,
                    padding: "2px 7px",
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {code}{" "}
                  <span style={{ opacity: 0.75 }}>×{count}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Top stores */}
          {topStores.length > 0 && (
            <div className="col-md-4">
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#aaa",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 5,
                }}
              >
                By Store
              </div>
              <div className="d-flex flex-wrap gap-1">
                {topStores.map(([store, count]) => (
                  <span
                    key={store}
                    style={{
                      background: "#d35400",
                      color: "#fff",
                      borderRadius: 4,
                      padding: "2px 7px",
                      fontSize: 11,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {store}{" "}
                    <span style={{ opacity: 0.75 }}>×{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Repeat codes */}
          {repeatCodes.length > 0 && (
            <div className="col-md-3">
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#aaa",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 5,
                }}
              >
                Repeat Codes
              </div>
              <div className="d-flex flex-wrap gap-1">
                {repeatCodes.map(([code, count]) => (
                  <span
                    key={code}
                    style={{
                      background: "#8e44ad",
                      color: "#fff",
                      borderRadius: 4,
                      padding: "2px 7px",
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: "monospace",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {code}{" "}
                    <span style={{ opacity: 0.75 }}>×{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────
const ErrorLogsScreen: React.FC = () => {
  // ── Tab ──────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<"system" | "code_audit" | "logon_audit" | "app_check">("code_audit");
  const navigate = useNavigate();

  // ── System Errors filters ────────────────────────────────────────────────
  const [requestIdInput, setRequestIdInput] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [resolvedFilter, setResolvedFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const [appliedFilters, setAppliedFilters] = useState<ErrorLogFilters>({
    page: 1,
    limit,
  });
  const [selectedLog, setSelectedLog] = useState<any>(null);

  const { data, isLoading, isError } = useGetErrorLogs(appliedFilters, activeTab === "system");
  const { mutateAsync: resolve, isLoading: resolving } = useResolveErrorLog();
  const { mutateAsync: deleteLog, isLoading: deleting } = useDeleteErrorLog();

  const logs: any[] = data?.data || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination.pages || 1;

  // ── Code Audit Log filters ────────────────────────────────────────────────
  const [auditReferenceId, setAuditReferenceId] = useState("");
  const [auditEventType, setAuditEventType] = useState("");
  const [auditOperation, setAuditOperation] = useState("");
  const [auditErrorCode, setAuditErrorCode] = useState("");
  const [auditCode, setAuditCode] = useState("");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditSuccess, setAuditSuccess] = useState<string>("false"); // "false" = failures only, "" = all, "true" = successes
  const [auditPage, setAuditPage] = useState(1);
  const [auditKeyName, setAuditKeyName] = useState("");
  const [auditStoreName, setAuditStoreName] = useState("");
  const [appliedAuditFilters, setAppliedAuditFilters] =
    useState<CollectionCodeAuditFilters>({ page: 1, limit, success: false });
  const [selectedAuditLog, setSelectedAuditLog] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const {
    data: auditData,
    isLoading: auditLoading,
    isError: auditError,
    refetch: auditRefetch,
  } = useGetCollectionCodeAuditLogs(appliedAuditFilters, activeTab === "code_audit");

  const auditLogs: any[] = auditData?.data || [];
  const auditPagination = auditData?.pagination || {};
  const auditTotalPages = auditPagination.pages || 1;

  useEffect(() => {
    if (!autoRefresh || activeTab !== "code_audit") return;
    const id = setInterval(() => { auditRefetch(); }, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, activeTab, auditRefetch]);

  // ── Logon Audit filters ───────────────────────────────────────────────────
  const [logonEventType, setLogonEventType] = useState("");
  const [logonUserRole, setLogonUserRole] = useState("");
  const [logonUserEmail, setLogonUserEmail] = useState("");
  const [logonIpAddress, setLogonIpAddress] = useState("");
  const [logonFrom, setLogonFrom] = useState("");
  const [logonTo, setLogonTo] = useState("");
  const [logonPage, setLogonPage] = useState(1);
  const [appliedLogonFilters, setAppliedLogonFilters] =
    useState<AuthAuditFilters>({ page: 1, limit });
  const [selectedLogonLog, setSelectedLogonLog] = useState<any>(null);

  const {
    data: logonData,
    isLoading: logonLoading,
    isError: logonError,
  } = useGetAuthAuditLogs(appliedLogonFilters, activeTab === "logon_audit");

  const { data: statsData } = useGetAuthAuditStats({}, activeTab === "logon_audit");

  const logonLogs: any[] = logonData?.data || [];
  const logonPagination = logonData?.pagination || {};
  const logonTotalPages = logonPagination.pages || 1;

  const stats = statsData?.data ?? statsData ?? null;

  // ── App Check filters ─────────────────────────────────────────────────────
  const [acReason, setAcReason] = useState("");
  const [acIp, setAcIp] = useState("");
  const [acFrom, setAcFrom] = useState("");
  const [acTo, setAcTo] = useState("");
  const [acPage, setAcPage] = useState(1);
  const acLimit = 20;
  const [appliedAcFilters, setAppliedAcFilters] = useState<AppCheckLogFilters>({ page: 1, limit: acLimit });

  const {
    data: acData,
    isLoading: acLoading,
    isError: acError,
  } = useGetAppCheckLogs(appliedAcFilters, activeTab === "app_check");

  const acLogs: any[] = acData?.data || [];
  const acStats = acData?.stats || {};
  const acPagination = acData?.pagination || {};
  const acTotalPages = acPagination.pages || 1;

  // ── Flagged Emails filters ────────────────────────────────────────────────
  const [feSearch, setFeSearch] = useState("");
  const [feReason, setFeReason] = useState("");
  const [feResolved, setFeResolved] = useState("false");
  const [feFrom, setFeFrom] = useState("");
  const [feTo, setFeTo] = useState("");
  const [fePage, setFePage] = useState(1);
  const feLimit = 20;
  const [appliedFeFilters, setAppliedFeFilters] = useState<FlaggedEmailFilters>({ page: 1, limit: feLimit, resolved: "false" });

  const {
    data: feData,
    isLoading: feLoading,
    isError: feError,
  } = useGetFlaggedEmails(appliedFeFilters, activeTab === "app_check");

  const feLogs: any[] = feData?.data || [];
  const feStats = feData?.stats || {};
  const fePagination = feData?.pagination || {};
  const feTotalPages = fePagination.pages || 1;

  const { mutateAsync: feResolve, isLoading: feResolving } = useResolveFlaggedEmail();
  const { mutateAsync: feDelete, isLoading: feDeleting } = useDeleteFlaggedEmail();

  // ── Handlers — system errors ─────────────────────────────────────────────
  const handleSearch = () => {
    const filters: ErrorLogFilters = { page: 1, limit };
    if (requestIdInput.trim()) filters.requestId = requestIdInput.trim();
    if (domainFilter) filters.domain = domainFilter;
    if (resolvedFilter !== "") filters.resolved = resolvedFilter;
    if (fromDate) filters.from = fromDate;
    if (toDate) filters.to = toDate;
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleClear = () => {
    setRequestIdInput("");
    setDomainFilter("");
    setResolvedFilter("");
    setFromDate("");
    setToDate("");
    setPage(1);
    setAppliedFilters({ page: 1, limit });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleResolve = async (id: string) => {
    try {
      await resolve({ id });
      toast.success("Error log marked as resolved");
      setSelectedLog(null);
    } catch (err: any) {
      toast.error(
        err?.displayMessage || err?.userMessage || "Failed to resolve error log"
      );
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Permanently delete this error log?")) return;
    try {
      await deleteLog({ id });
      toast.success("Error log deleted");
      setSelectedLog(null);
    } catch (err: any) {
      toast.error(
        err?.displayMessage || err?.userMessage || "Failed to delete error log"
      );
    }
  };

  // ── Handlers — code audit logs ────────────────────────────────────────────
  const handleAuditSearch = () => {
    const filters: CollectionCodeAuditFilters = { page: 1, limit };
    if (auditReferenceId.trim()) filters.request_id = auditReferenceId.trim();
    if (auditEventType) filters.event_type = auditEventType;
    if (auditOperation) filters.operation = auditOperation;
    if (auditErrorCode.trim()) filters.error_code = auditErrorCode.trim();
    if (auditCode.trim()) filters.code = auditCode.trim();
    if (auditFrom) filters.from = auditFrom;
    if (auditTo) filters.to = auditTo;
    if (auditSuccess === "false") filters.success = false;
    else if (auditSuccess === "true") filters.success = true;
    if (auditKeyName.trim()) filters.key_name = auditKeyName.trim();
    if (auditStoreName.trim()) filters.store_name = auditStoreName.trim();
    setAuditPage(1);
    setAppliedAuditFilters(filters);
  };

  const handleAuditClear = () => {
    setAuditReferenceId("");
    setAuditEventType("");
    setAuditOperation("");
    setAuditErrorCode("");
    setAuditCode("");
    setAuditFrom("");
    setAuditTo("");
    setAuditSuccess("false");
    setAuditKeyName("");
    setAuditStoreName("");
    setAuditPage(1);
    setAppliedAuditFilters({ page: 1, limit, success: false });
  };

  const handleAuditPageChange = (newPage: number) => {
    setAuditPage(newPage);
    setAppliedAuditFilters((prev) => ({ ...prev, page: newPage }));
  };

  // ── Handlers — logon audit logs ──────────────────────────────────────────
  const handleLogonSearch = () => {
    const filters: AuthAuditFilters = { page: 1, limit };
    if (logonEventType) filters.eventType = logonEventType;
    if (logonUserRole) filters.userRole = logonUserRole;
    if (logonUserEmail.trim()) filters.userEmail = logonUserEmail.trim();
    if (logonIpAddress.trim()) filters.ipAddress = logonIpAddress.trim();
    if (logonFrom) filters.from = logonFrom;
    if (logonTo) filters.to = logonTo;
    setLogonPage(1);
    setAppliedLogonFilters(filters);
  };

  const handleLogonClear = () => {
    setLogonEventType("");
    setLogonUserRole("");
    setLogonUserEmail("");
    setLogonIpAddress("");
    setLogonFrom("");
    setLogonTo("");
    setLogonPage(1);
    setAppliedLogonFilters({ page: 1, limit });
  };

  const handleLogonPageChange = (newPage: number) => {
    setLogonPage(newPage);
    setAppliedLogonFilters((prev) => ({ ...prev, page: newPage }));
  };

  // ── Handlers — App Check logs ─────────────────────────────────────────────
  const handleAcSearch = () => {
    const filters: AppCheckLogFilters = { page: 1, limit: acLimit };
    if (acReason) filters.reason = acReason;
    if (acIp.trim()) filters.ip = acIp.trim();
    if (acFrom) filters.from = acFrom;
    if (acTo) filters.to = acTo;
    setAcPage(1);
    setAppliedAcFilters(filters);
  };

  const handleAcClear = () => {
    setAcReason("");
    setAcIp("");
    setAcFrom("");
    setAcTo("");
    setAcPage(1);
    setAppliedAcFilters({ page: 1, limit: acLimit });
  };

  const handleAcPageChange = (newPage: number) => {
    setAcPage(newPage);
    setAppliedAcFilters((prev) => ({ ...prev, page: newPage }));
  };

  // ── Handlers — flagged emails ─────────────────────────────────────────────
  const handleFeSearch = () => {
    const filters: FlaggedEmailFilters = { page: 1, limit: feLimit };
    if (feSearch.trim()) filters.search = feSearch.trim();
    if (feReason) filters.reason = feReason;
    if (feResolved !== "") filters.resolved = feResolved;
    if (feFrom) filters.from = feFrom;
    if (feTo) filters.to = feTo;
    setFePage(1);
    setAppliedFeFilters(filters);
  };

  const handleFeClear = () => {
    setFeSearch("");
    setFeReason("");
    setFeResolved("false");
    setFeFrom("");
    setFeTo("");
    setFePage(1);
    setAppliedFeFilters({ page: 1, limit: feLimit, resolved: "false" });
  };

  const handleFePageChange = (newPage: number) => {
    setFePage(newPage);
    setAppliedFeFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleFeResolve = async (id: string) => {
    try {
      await feResolve({ id });
      toast.success("Flagged email marked as resolved");
    } catch (err: any) {
      toast.error(err?.displayMessage || err?.userMessage || "Failed to resolve");
    }
  };

  const handleFeDelete = async (id: string) => {
    if (!window.confirm("Permanently delete this flagged email record?")) return;
    try {
      await feDelete({ id });
      toast.success("Flagged email deleted");
    } catch (err: any) {
      toast.error(err?.displayMessage || err?.userMessage || "Failed to delete");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="content-wrapper">
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
      <Header title="Error Logs" />
      {/* Tab switcher */}
      <div className="main-wrapper px-4 pt-4 pb-0">
        <ul
          className="nav nav-tabs"
          style={{ borderBottom: "2px solid #dee2e6" }}
        >
          <li className="nav-item">
            <button
              className={`nav-link${
                activeTab === "system" ? " active fw-semibold" : ""
              }`}
              onClick={() => setActiveTab("system")}
              style={{ cursor: "pointer" }}
            >
              <i className="fa-solid fa-triangle-exclamation me-2 text-danger" />
              System Errors
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link${
                activeTab === "code_audit" ? " active fw-semibold" : ""
              }`}
              onClick={() => setActiveTab("code_audit")}
              style={{ cursor: "pointer" }}
            >
              <i className="fa-solid fa-barcode me-2 text-warning" />
              Code Audit Logs
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link${
                activeTab === "logon_audit" ? " active fw-semibold" : ""
              }`}
              onClick={() => setActiveTab("logon_audit")}
              style={{ cursor: "pointer" }}
            >
              <i className="fa-solid fa-shield-halved me-2 text-primary" />
              Logon Audit
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link${
                activeTab === "app_check" ? " active fw-semibold" : ""
              }`}
              onClick={() => setActiveTab("app_check")}
              style={{ cursor: "pointer" }}
            >
              <i className="fa-solid fa-robot me-2 text-danger" />
              Bot Detection
            </button>
          </li>
        </ul>
      </div>
      {/* ── System Errors Tab ─────────────────────────────────────────────── */}
      {activeTab === "system" && (
        <div className="main-wrapper p-4">
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    Reference ID
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Paste reference ID…"
                    value={requestIdInput}
                    onChange={(e) => setRequestIdInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <div className="col-md-2">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    Domain
                  </label>
                  <select
                    className="form-select"
                    value={domainFilter}
                    onChange={(e) => setDomainFilter(e.target.value)}
                  >
                    <option value="">All domains</option>
                    {DOMAINS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    Status
                  </label>
                  <select
                    className="form-select"
                    value={resolvedFilter}
                    onChange={(e) => setResolvedFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="false">Unresolved</option>
                    <option value="true">Resolved</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    From
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    To
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-md-1 d-flex gap-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleSearch}
                  >
                    <i className="fa-solid fa-magnifying-glass" />
                  </button>
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={handleClear}
                    title="Clear filters"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          {isLoading ? (
            <CustomLoader />
          ) : isError ? (
            <div className="alert alert-warning">
              Failed to load error logs. Please try again.
            </div>
          ) : logs.length === 0 ? (
            <NoRecordFound />
          ) : (
            <>
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-hover mb-0"
                      style={{ fontSize: 13 }}
                    >
                      <thead className="table-light">
                        <tr>
                          <th>Timestamp</th>
                          <th>Reference ID</th>
                          <th>Domain</th>
                          <th>Error Code</th>
                          <th>Message</th>
                          <th>Status</th>
                          <th>HTTP</th>
                          <th>Resolved</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log: any) => (
                          <tr key={log._id} style={{ verticalAlign: "middle" }}>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {new Date(log.createdAt).toLocaleString()}
                            </td>
                            <td>
                              <code
                                style={{
                                  fontSize: 11,
                                  cursor: "pointer",
                                  color: "#2980b9",
                                }}
                                title="Click to view detail"
                                onClick={() => setSelectedLog(log)}
                              >
                                {log.requestId?.substring(0, 8)}…
                              </code>
                            </td>
                            <td>
                              <DomainBadge domain={log.domain} />
                            </td>
                            <td>
                              <code style={{ fontSize: 11 }}>
                                {log.errorCode}
                              </code>
                            </td>
                            <td
                              style={{
                                maxWidth: 260,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {log.message}
                            </td>
                            <td>
                              {log.userId ? (
                                <span style={{ fontSize: 11 }}>
                                  <span className="badge bg-secondary me-1">
                                    {log.userRole}
                                  </span>
                                </span>
                              ) : (
                                <span style={{ fontSize: 11, color: "#aaa" }}>
                                  —
                                </span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  log.statusCode >= 500
                                    ? "bg-danger"
                                    : "bg-warning text-dark"
                                }`}
                              >
                                {log.statusCode}
                              </span>
                            </td>
                            <td>
                              {log.resolved ? (
                                <span className="badge bg-success">
                                  Resolved
                                </span>
                              ) : (
                                <span className="badge bg-danger">Open</span>
                              )}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                title="View detail"
                                onClick={() => setSelectedLog(log)}
                              >
                                <i className="fa-regular fa-eye" />
                              </button>
                              {!log.resolved && (
                                <button
                                  className="btn btn-sm btn-outline-success me-1"
                                  title="Mark as resolved"
                                  disabled={resolving}
                                  onClick={() => handleResolve(log._id)}
                                >
                                  <i className="fa-solid fa-check" />
                                </button>
                              )}
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Delete"
                                disabled={deleting}
                                onClick={() => handleDelete(log._id)}
                              >
                                <i className="fa-regular fa-trash-can" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-3 d-flex justify-content-between align-items-center">
                <span style={{ fontSize: 13, color: "#666" }}>
                  Total: <strong>{pagination.total || 0}</strong> errors
                </span>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  handlePrevPage={() => page > 1 && handlePageChange(page - 1)}
                  handleNextPage={() =>
                    page < totalPages && handlePageChange(page + 1)
                  }
                  handleRowLimitChange={() => {}}
                />
              </div>
            </>
          )}
        </div>
      )}{" "}
      {/* end system tab */}
      {/* Detail modal — rendered outside tab blocks so it works on both tabs */}
      {selectedLog && (
        <DetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onResolve={handleResolve}
          resolving={resolving}
        />
      )}
      {/* Code Audit detail modal */}
      {selectedAuditLog && (
        <CodeAuditDetailModal
          log={selectedAuditLog}
          onClose={() => setSelectedAuditLog(null)}
        />
      )}
      {/* ── Code Audit Logs Tab ────────────────────────────────────────────── */}
      {activeTab === "code_audit" && (
        <div className="main-wrapper p-4">
          {/* Audit filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                {/* Row 1 */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Reference ID
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Paste reference ID…"
                    value={auditReferenceId}
                    onChange={(e) => setAuditReferenceId(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAuditSearch()}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Operation
                  </label>
                  <select
                    className="form-select"
                    value={auditOperation}
                    onChange={(e) => setAuditOperation(e.target.value)}
                  >
                    <option value="">All operations</option>
                    <optgroup label="Key ingestion">
                      <option value="registration">Registration</option>
                      <option value="keyring_drop_off">Key Drop-Off</option>
                      <option value="placement_confirmation">Placement Confirmation</option>
                    </optgroup>
                    <optgroup label="Key collection">
                      <option value="collection">Collection Code</option>
                      <option value="owner">Owner Collection Code</option>
                      <option value="owner_return_code">Owner Return Code</option>
                    </optgroup>
                    <optgroup label="Legacy">
                      <option value="keyring_scan">Keyring Scan</option>
                      <option value="keyring_link">Keyring Link</option>
                    </optgroup>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Event Type
                  </label>
                  <select
                    className="form-select"
                    value={auditEventType}
                    onChange={(e) => setAuditEventType(e.target.value)}
                  >
                    <option value="">All types</option>
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Result
                  </label>
                  <select
                    className="form-select"
                    value={auditSuccess}
                    onChange={(e) => setAuditSuccess(e.target.value)}
                  >
                    <option value="false">Failures only</option>
                    <option value="">All events</option>
                    <option value="true">Successes only</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Error Code
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. INVALID_CODE"
                    value={auditErrorCode}
                    onChange={(e) => setAuditErrorCode(e.target.value)}
                  />
                </div>
                <div className="col-md-1 d-flex gap-2 align-items-end">
                  <button className="btn btn-primary w-100" onClick={handleAuditSearch}>
                    <i className="fa-solid fa-magnifying-glass" />
                  </button>
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={handleAuditClear}
                    title="Clear"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
                {/* Row 2 — date range */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    From
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={auditFrom}
                    onChange={(e) => setAuditFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    To
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={auditTo}
                    onChange={(e) => setAuditTo(e.target.value)}
                  />
                </div>
                {/* Row 3 — key and store name search */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Key Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by key name…"
                    value={auditKeyName}
                    onChange={(e) => setAuditKeyName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAuditSearch()}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Store Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by store name…"
                    value={auditStoreName}
                    onChange={(e) => setAuditStoreName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAuditSearch()}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Audit results */}
          {auditLoading ? (
            <CustomLoader />
          ) : auditError ? (
            <div className="alert alert-warning">
              Failed to load code audit logs. Please try again.
            </div>
          ) : auditLogs.length === 0 ? (
            <NoRecordFound />
          ) : (
            <>
              {/* Auto-refresh toggle */}
              <div className="d-flex justify-content-end mb-2">
                <button
                  className={`btn btn-sm ${autoRefresh ? "btn-danger" : "btn-outline-secondary"}`}
                  onClick={() => setAutoRefresh((v) => !v)}
                  title={autoRefresh ? "Stop auto-refresh" : "Auto-refresh every 30s"}
                >
                  {autoRefresh ? (
                    <>
                      <span
                        style={{
                          display: "inline-block", width: 8, height: 8,
                          borderRadius: "50%", background: "#fff",
                          marginRight: 6, animation: "pulse 1s infinite",
                        }}
                      />
                      Live
                    </>
                  ) : (
                    <><i className="fa-solid fa-rotate me-1" />Auto-refresh</>
                  )}
                </button>
              </div>
              {/* Success rate summary */}
              {(() => {
                const total = auditLogs.length;
                const successes = auditLogs.filter((l: any) => l.success).length;
                const failures = total - successes;
                const rate = total > 0 ? Math.round((successes / total) * 100) : null;
                return (
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    <span className="badge fs-6 fw-semibold" style={{ background: "#2980b9", padding: "6px 14px" }}>
                      {total} total
                    </span>
                    <span className="badge fs-6 fw-semibold" style={{ background: "#27ae60", padding: "6px 14px" }}>
                      {successes} succeeded
                    </span>
                    <span className="badge fs-6 fw-semibold" style={{ background: "#e74c3c", padding: "6px 14px" }}>
                      {failures} failed
                    </span>
                    {rate !== null && (
                      <span className="badge fs-6 fw-semibold" style={{ background: rate >= 80 ? "#16a085" : rate >= 50 ? "#e67e22" : "#c0392b", padding: "6px 14px" }}>
                        {rate}% success rate
                      </span>
                    )}
                  </div>
                );
              })()}
              <SpikeChart logs={auditLogs} />
              <FailureDigest logs={auditLogs} />
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-hover mb-0"
                      style={{ fontSize: 13 }}
                    >
                       <thead className="table-light">
                         <tr>
                           <th>Timestamp</th>
                            <th>Reference ID</th>
                            <th>Code</th>
                            <th>Key</th>
                            <th>Operation</th>
                           <th>Event</th>
                           <th>Error Code</th>
                           <th>Failure Reason</th>
                           <th>Actor</th>
                           <th>Store</th>
                           <th>Details</th>
                         </tr>
                       </thead>
                      <tbody>
                        {(() => {
                          const failsByCode: Record<string, number> = {};
                          const failsByKey: Record<string, number> = {};
                          auditLogs.forEach((l: any) => {
                            if (!l.success) {
                              if (l.code && l.code !== '***') failsByCode[l.code] = (failsByCode[l.code] || 0) + 1;
                              if (l.keyId) failsByKey[String(l.keyId)] = (failsByKey[String(l.keyId)] || 0) + 1;
                            }
                          });
                          const isRepeat = (l: any) => !l.success && (
                            (l.code && failsByCode[l.code] >= 3) ||
                            (l.keyId && failsByKey[String(l.keyId)] >= 3)
                          );
                          return auditLogs.map((log: any) => (
                            <tr key={log._id} style={{ verticalAlign: "middle", background: isRepeat(log) ? "#fff0f0" : undefined }}>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {log.eventTimestamp
                                ? new Date(
                                    log.eventTimestamp * 1000
                                  ).toLocaleString()
                                : log.createdAt
                                ? new Date(log.createdAt).toLocaleString()
                                : "—"}
                            </td>
                            <td>
                              {log.requestId ? (
                                <code
                                  style={{ fontSize: 11, color: "#2980b9" }}
                                >
                                  {log.requestId.substring(0, 22)}…
                                </code>
                              ) : (
                                <span style={{ color: "#aaa", fontSize: 11 }}>
                                  —
                                </span>
                              )}
                             </td>
                             <td>
                               {log.code ? (
                                 <code style={{ fontSize: 12, color: "#27ae60", fontWeight: 600 }}>
                                   {log.code}
                                 </code>
                               ) : (
                                 <span style={{ color: "#aaa", fontSize: 11 }}>—</span>
                               )}
                             </td>
                             <td style={{ fontSize: 12 }}>
                               {log.keyName ? (
                                 log.keyId ? (
                                   <div className="d-flex align-items-center gap-2">
                                     <span
                                       style={{ fontWeight: 500, cursor: "pointer", color: "#72bcb8" }}
                                       title="View key details"
                                       onClick={() => navigate("/keymanagementdetail", { state: { id: String(log.keyId) } })}
                                     >
                                       {log.keyName}
                                       <i className="fa-solid fa-arrow-up-right-from-square ms-1" style={{ fontSize: 10 }} />
                                     </span>
                                     <i
                                       className="fa-solid fa-microscope"
                                       style={{ fontSize: 11, cursor: "pointer", color: "#888" }}
                                       title="Investigate this key"
                                       onClick={() => navigate("/key-investigation", { state: { id: String(log.keyId) } })}
                                     />
                                   </div>
                                 ) : (
                                   <span style={{ fontWeight: 500 }}>{log.keyName}</span>
                                 )
                               ) : log.keyId ? (
                                 <i
                                   className="fa-solid fa-microscope"
                                   style={{ fontSize: 11, cursor: "pointer", color: "#888" }}
                                   title="Investigate this key"
                                   onClick={() => navigate("/key-investigation", { state: { id: String(log.keyId) } })}
                                 />
                               ) : (
                                 <span style={{ color: "#aaa", fontSize: 11 }}>—</span>
                               )}
                             </td>
                              <td>
                                <OperationBadge operation={log.operation} />
                             </td>
                             <td>
                               <EventTypeBadge type={log.eventType} />
                             </td>
                            <td>
                              {log.errorCode ? (
                                <code style={{ fontSize: 11 }}>
                                  {log.errorCode}
                                </code>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td
                              style={{
                                maxWidth: 220,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {resolveFailureReason(log.errorCode, log.failureReason)}
                            </td>
                            <td style={{ fontSize: 12 }}>
                              {log.actorName || "—"}
                              {log.actorType && (
                                <span
                                  className="badge bg-secondary ms-1"
                                  style={{ fontSize: 10 }}
                                >
                                  {log.actorType.replace(/_/g, " ")}
                                </span>
                              )}
                            </td>
                             <td style={{ fontSize: 12 }}>
                               {log.storeName || "—"}
                             </td>
                             <td>
                               <button
                                 className="btn btn-sm btn-outline-primary"
                                 title="View detail"
                                 onClick={() => setSelectedAuditLog(log)}
                               >
                                 <i className="fa-regular fa-eye" />
                               </button>
                             </td>
                          </tr>
                        ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-3 d-flex justify-content-between align-items-center">
                <span style={{ fontSize: 13, color: "#666" }}>
                  Total: <strong>{auditPagination.total || 0}</strong> attempts
                </span>
                <Pagination
                  currentPage={auditPage}
                  totalPages={auditTotalPages}
                  handlePrevPage={() =>
                    auditPage > 1 && handleAuditPageChange(auditPage - 1)
                  }
                  handleNextPage={() =>
                    auditPage < auditTotalPages &&
                    handleAuditPageChange(auditPage + 1)
                  }
                  handleRowLimitChange={() => {}}
                />
              </div>
            </>
          )}
        </div>
      )}{" "}
      {/* end code_audit tab */}

      {/* ── Logon Audit Tab ───────────────────────────────────────────────── */}
      {activeTab === "logon_audit" && (
        <div className="main-wrapper p-4">

          {/* Stats bar */}
          {stats && (
            <div className="row g-3 mb-4">
              {[
                {
                  label: "Total Events",
                  value: stats.totalEvents ?? "—",
                  colour: "#2980b9",
                  icon: "fa-list",
                },
                {
                  label: "Login Failures",
                  value:
                    stats.byEventType?.LOGIN_FAILED ??
                    stats.eventTypeCounts?.LOGIN_FAILED ??
                    "—",
                  colour: "#e74c3c",
                  icon: "fa-circle-xmark",
                },
                {
                  label: "Login Successes",
                  value:
                    stats.byEventType?.LOGIN_SUCCESS ??
                    stats.eventTypeCounts?.LOGIN_SUCCESS ??
                    "—",
                  colour: "#27ae60",
                  icon: "fa-circle-check",
                },
                {
                  label: "OTP Failures",
                  value:
                    stats.byEventType?.OTP_FAILED ??
                    stats.eventTypeCounts?.OTP_FAILED ??
                    "—",
                  colour: "#e67e22",
                  icon: "fa-key",
                },
                {
                  label: "Top Failed IP (24h)",
                  value: stats.topFailedIps?.[0]?.ipAddress ?? stats.topFailedIps?.[0]?.ip ?? "—",
                  subValue: stats.topFailedIps?.[0]
                    ? `${stats.topFailedIps[0].count ?? stats.topFailedIps[0].failureCount ?? ""} attempts`
                    : undefined,
                  colour: "#8e44ad",
                  icon: "fa-network-wired",
                },
              ].map((card) => (
                <div className="col-md" key={card.label}>
                  <div
                    className="card h-100"
                    style={{
                      borderLeft: `4px solid ${card.colour}`,
                      borderRadius: 6,
                    }}
                  >
                    <div className="card-body py-3 px-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i
                          className={`fa-solid ${card.icon}`}
                          style={{ color: card.colour, fontSize: 14 }}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#6c757d",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {card.label}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: card.colour,
                          lineHeight: 1.2,
                        }}
                      >
                        {card.value}
                      </div>
                      {card.subValue && (
                        <div style={{ fontSize: 11, color: "#999" }}>
                          {card.subValue}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Event Type
                  </label>
                  <select
                    className="form-select"
                    value={logonEventType}
                    onChange={(e) => setLogonEventType(e.target.value)}
                  >
                    <option value="">All types</option>
                    <option value="LOGIN_FAILED">Login Failed</option>
                    <option value="LOGIN_SUCCESS">Login Success</option>
                    <option value="OTP_FAILED">OTP Failed</option>
                    <option value="OTP_SUCCESS">OTP Success</option>
                    <option value="LOGOUT">Logout</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    User Role
                  </label>
                  <select
                    className="form-select"
                    value={logonUserRole}
                    onChange={(e) => setLogonUserRole(e.target.value)}
                  >
                    <option value="">All roles</option>
                    <option value="OWNER">Owner</option>
                    <option value="HOST">Host</option>
                    <option value="ADMIN">Admin</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Email
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="exact email address…"
                    value={logonUserEmail}
                    onChange={(e) => setLogonUserEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogonSearch()}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    IP Address
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 192.168.1.1"
                    value={logonIpAddress}
                    onChange={(e) => setLogonIpAddress(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogonSearch()}
                  />
                </div>
                <div className="col-md-1 d-flex gap-2 align-items-end">
                  <button className="btn btn-primary w-100" onClick={handleLogonSearch}>
                    <i className="fa-solid fa-magnifying-glass" />
                  </button>
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={handleLogonClear}
                    title="Clear filters"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
                {/* Row 2 — date range */}
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    From
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={logonFrom}
                    onChange={(e) => setLogonFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    To
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={logonTo}
                    onChange={(e) => setLogonTo(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          {logonLoading ? (
            <CustomLoader />
          ) : logonError ? (
            <div className="alert alert-warning">
              Failed to load logon audit logs. Please try again.
            </div>
          ) : logonLogs.length === 0 ? (
            <NoRecordFound />
          ) : (
            <>
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-hover mb-0"
                      style={{ fontSize: 13 }}
                    >
                      <thead className="table-light">
                        <tr>
                          <th>Timestamp</th>
                          <th>Event</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>IP Address</th>
                          <th>Failure Reason</th>
                          <th>Request ID</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logonLogs.map((log: any) => (
                          <tr key={log._id} style={{ verticalAlign: "middle" }}>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {log.timestamp
                                ? new Date(log.timestamp).toLocaleString()
                                : log.createdAt
                                ? new Date(log.createdAt).toLocaleString()
                                : "—"}
                            </td>
                            <td>
                              <AuthEventBadge type={log.eventType} />
                            </td>
                            <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {log.userEmail || (
                                <span style={{ color: "#aaa" }}>—</span>
                              )}
                            </td>
                            <td>
                              <RoleBadge role={log.userRole} />
                            </td>
                            <td>
                              <code style={{ fontSize: 12 }}>
                                {log.ipAddress || "—"}
                              </code>
                            </td>
                            <td
                              style={{
                                maxWidth: 220,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: log.failureReason ? "#c0392b" : "#aaa",
                              }}
                            >
                              {resolveFailureReason(log.errorCode, log.failureReason)}
                            </td>
                            <td>
                              {log.requestId ? (
                                <code style={{ fontSize: 11, color: "#2980b9" }}>
                                  {log.requestId.substring(0, 14)}…
                                </code>
                              ) : (
                                <span style={{ color: "#aaa", fontSize: 11 }}>—</span>
                              )}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="View full detail"
                                onClick={() => setSelectedLogonLog(log)}
                              >
                                <i className="fa-regular fa-eye" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-3 d-flex justify-content-between align-items-center">
                <span style={{ fontSize: 13, color: "#666" }}>
                  Total: <strong>{logonPagination.total || 0}</strong> events
                </span>
                <Pagination
                  currentPage={logonPage}
                  totalPages={logonTotalPages}
                  handlePrevPage={() =>
                    logonPage > 1 && handleLogonPageChange(logonPage - 1)
                  }
                  handleNextPage={() =>
                    logonPage < logonTotalPages &&
                    handleLogonPageChange(logonPage + 1)
                  }
                  handleRowLimitChange={() => {}}
                />
              </div>
            </>
          )}
        </div>
      )}{" "}
      {/* end logon_audit tab */}

      {/* Logon Audit detail modal */}
      {selectedLogonLog && (
        <LogonAuditDetailModal
          log={selectedLogonLog}
          onClose={() => setSelectedLogonLog(null)}
        />
      )}

      {/* ── Bot Detection Tab ──────────────────────────────────────────────── */}
      {activeTab === "app_check" && (
        <div className="main-wrapper p-4">

          {/* Summary stat cards */}
          {!acLoading && acStats.total > 0 && (
            <div className="row g-3 mb-4">
              {[
                { label: "Total Unattested", value: acStats.total ?? 0, icon: "fa-solid fa-ban", colour: "#e74c3c" },
                { label: "Missing Token",    value: acStats.missing ?? 0, icon: "fa-solid fa-circle-question", colour: "#e67e22" },
                { label: "Invalid Token",    value: acStats.invalid ?? 0, icon: "fa-solid fa-triangle-exclamation", colour: "#c0392b" },
                { label: "Unique IPs",       value: acStats.uniqueIps ?? 0, icon: "fa-solid fa-network-wired", colour: "#8e44ad" },
              ].map(({ label, value, icon, colour }) => (
                <div key={label} className="col-6 col-md-3">
                  <div className="card h-100" style={{ borderLeft: `4px solid ${colour}` }}>
                    <div className="card-body py-3 px-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className={icon} style={{ color: colour, fontSize: 13 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {label}
                        </span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#212529" }}>{value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Top offending IPs */}
          {!acLoading && acStats.topIps?.length > 0 && (
            <div className="card mb-4" style={{ borderLeft: "4px solid #c0392b", background: "#fff8f8" }}>
              <div className="card-body py-2 px-3">
                <p className="mb-2 fw-semibold" style={{ fontSize: 13 }}>
                  <i className="fa-solid fa-fire me-2 text-danger" />
                  Top Offending IPs
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {acStats.topIps.map((entry: any) => (
                    <button
                      key={entry._id}
                      className="btn btn-sm"
                      style={{ background: "#c0392b", color: "#fff", fontFamily: "monospace", fontSize: 12 }}
                      title="Filter by this IP"
                      onClick={() => {
                        setAcIp(entry._id);
                        setAppliedAcFilters({ page: 1, limit: acLimit, ip: entry._id });
                        setAcPage(1);
                      }}
                    >
                      {entry._id} <span style={{ opacity: 0.75 }}>×{entry.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Reason</label>
                  <select className="form-select" value={acReason} onChange={(e) => setAcReason(e.target.value)}>
                    <option value="">All reasons</option>
                    <option value="missing_token">Missing token</option>
                    <option value="invalid_token">Invalid token</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>IP Address</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 1.2.3.4"
                    value={acIp}
                    onChange={(e) => setAcIp(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAcSearch()}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>From</label>
                  <input type="date" className="form-control" value={acFrom} onChange={(e) => setAcFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>To</label>
                  <input type="date" className="form-control" value={acTo} onChange={(e) => setAcTo(e.target.value)} />
                </div>
                <div className="col-md-3 d-flex gap-2 align-items-end">
                  <button className="btn btn-primary" onClick={handleAcSearch}>
                    <i className="fa-solid fa-magnifying-glass me-1" /> Apply
                  </button>
                  <button className="btn btn-outline-secondary" onClick={handleAcClear}>
                    <i className="fa-solid fa-xmark me-1" /> Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          {acLoading ? (
            <CustomLoader />
          ) : acError ? (
            <div className="alert alert-warning">Failed to load App Check logs. Please try again.</div>
          ) : acLogs.length === 0 ? (
            <NoRecordFound />
          ) : (
            <>
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ whiteSpace: "nowrap" }}>Timestamp</th>
                          <th>Reason</th>
                          <th>IP Address</th>
                          <th>Path</th>
                          <th>User Agent</th>
                          <th>Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {acLogs.map((log: any, i: number) => (
                          <tr key={i} style={{ verticalAlign: "middle" }}>
                            <td style={{ whiteSpace: "nowrap", color: "#6c757d" }}>
                              {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}
                            </td>
                            <td>
                              <span style={{
                                background: log.reason === "missing_token" ? "#e67e22" : "#c0392b",
                                color: "#fff", padding: "2px 8px", borderRadius: 4,
                                fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                              }}>
                                {log.reason === "missing_token" ? "Missing token" : "Invalid token"}
                              </span>
                            </td>
                            <td>
                              <code
                                style={{ fontSize: 12, cursor: "pointer", color: "#2980b9" }}
                                title="Filter by this IP"
                                onClick={() => {
                                  setAcIp(log.ip);
                                  setAppliedAcFilters({ page: 1, limit: acLimit, ip: log.ip });
                                  setAcPage(1);
                                }}
                              >
                                {log.ip || "—"}
                              </code>
                            </td>
                            <td><code style={{ fontSize: 11 }}>{log.path || "—"}</code></td>
                            <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, color: "#555" }}>
                              {log.userAgent || "—"}
                            </td>
                            <td>
                              <span className={`badge ${log.enforce ? "bg-danger" : "bg-secondary"}`}>
                                {log.enforce ? "Enforcing" : "Monitoring"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-3 d-flex justify-content-between align-items-center">
                <span style={{ fontSize: 13, color: "#666" }}>
                  Total: <strong>{acPagination.total || 0}</strong> unattested requests
                </span>
                <Pagination
                  currentPage={acPage}
                  totalPages={acTotalPages}
                  handlePrevPage={() => acPage > 1 && handleAcPageChange(acPage - 1)}
                  handleNextPage={() => acPage < acTotalPages && handleAcPageChange(acPage + 1)}
                  handleRowLimitChange={() => {}}
                />
              </div>
            </>
          )}

          {/* ── Flagged Emails ──────────────────────────────────────────── */}
          <hr className="my-4" />
          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="fa-solid fa-flag text-danger" style={{ fontSize: 16 }} />
            <h6 className="mb-0 fw-bold" style={{ fontSize: 15 }}>Flagged Emails</h6>
            <span className="badge bg-secondary" style={{ fontSize: 11 }}>
              Accounts deleted due to OTP timeout or cleanup job
            </span>
          </div>

          {/* Flagged email stat cards */}
          {!feLoading && (feStats.total ?? 0) > 0 && (
            <div className="row g-3 mb-4">
              {[
                { label: "Total Flagged",  value: feStats.total ?? 0,       icon: "fa-solid fa-flag",              colour: "#e74c3c" },
                { label: "OTP Timeout",    value: feStats.otpTimeout ?? 0,  icon: "fa-solid fa-clock",             colour: "#e67e22" },
                { label: "Cleanup Job",    value: feStats.cleanupJob ?? 0,  icon: "fa-solid fa-broom",             colour: "#8e44ad" },
                { label: "Unresolved",     value: feStats.unresolved ?? 0,  icon: "fa-solid fa-circle-exclamation",colour: "#c0392b" },
              ].map(({ label, value, icon, colour }) => (
                <div key={label} className="col-6 col-md-3">
                  <div className="card h-100" style={{ borderLeft: `4px solid ${colour}` }}>
                    <div className="card-body py-3 px-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className={icon} style={{ color: colour, fontSize: 13 }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {label}
                        </span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#212529" }}>{value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Flagged email filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Email</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search email…"
                    value={feSearch}
                    onChange={(e) => setFeSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleFeSearch()}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Reason</label>
                  <select className="form-select" value={feReason} onChange={(e) => setFeReason(e.target.value)}>
                    <option value="">All reasons</option>
                    <option value="otp_timeout">OTP Timeout</option>
                    <option value="cleanup_job">Cleanup Job</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Status</label>
                  <select className="form-select" value={feResolved} onChange={(e) => setFeResolved(e.target.value)}>
                    <option value="false">Unresolved</option>
                    <option value="">All</option>
                    <option value="true">Resolved</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>From</label>
                  <input type="date" className="form-control" value={feFrom} onChange={(e) => setFeFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>To</label>
                  <input type="date" className="form-control" value={feTo} onChange={(e) => setFeTo(e.target.value)} />
                </div>
                <div className="col-md-1 d-flex gap-2 align-items-end">
                  <button className="btn btn-primary w-100" onClick={handleFeSearch}>
                    <i className="fa-solid fa-magnifying-glass" />
                  </button>
                  <button className="btn btn-outline-secondary w-100" onClick={handleFeClear} title="Clear">
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Flagged email table */}
          {feLoading ? (
            <CustomLoader />
          ) : feError ? (
            <div className="alert alert-warning">Failed to load flagged emails. Please try again.</div>
          ) : feLogs.length === 0 ? (
            <NoRecordFound />
          ) : (
            <>
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ whiteSpace: "nowrap" }}>Flagged At</th>
                          <th>Email</th>
                          <th>Reason</th>
                          <th>Registration IP</th>
                          <th>User Agent</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feLogs.map((log: any) => (
                          <tr key={log._id} style={{ verticalAlign: "middle" }}>
                            <td style={{ whiteSpace: "nowrap", color: "#6c757d" }}>
                              {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}
                            </td>
                            <td>
                              <code style={{ fontSize: 12 }}>{log.email}</code>
                            </td>
                            <td>
                              <span style={{
                                background: log.reason === "otp_timeout" ? "#e67e22" : "#8e44ad",
                                color: "#fff", padding: "2px 8px", borderRadius: 4,
                                fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                              }}>
                                {log.reason === "otp_timeout" ? "OTP Timeout" : "Cleanup Job"}
                              </span>
                            </td>
                            <td>
                              {log.registrationIp
                                ? <code style={{ fontSize: 12, color: "#2980b9" }}>{log.registrationIp}</code>
                                : <span style={{ color: "#aaa" }}>—</span>}
                            </td>
                            <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, color: "#555" }}>
                              {log.registrationUserAgent || "—"}
                            </td>
                            <td>
                              {log.resolved
                                ? <span className="badge bg-success">Resolved</span>
                                : <span className="badge bg-danger">Open</span>}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {!log.resolved && (
                                <button
                                  className="btn btn-sm btn-outline-success me-1"
                                  title="Mark as resolved"
                                  disabled={feResolving}
                                  onClick={() => handleFeResolve(log._id)}
                                >
                                  <i className="fa-solid fa-check" />
                                </button>
                              )}
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Delete record"
                                disabled={feDeleting}
                                onClick={() => handleFeDelete(log._id)}
                              >
                                <i className="fa-regular fa-trash-can" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-3 d-flex justify-content-between align-items-center">
                <span style={{ fontSize: 13, color: "#666" }}>
                  Total: <strong>{fePagination.total || 0}</strong> flagged emails
                </span>
                <Pagination
                  currentPage={fePage}
                  totalPages={feTotalPages}
                  handlePrevPage={() => fePage > 1 && handleFePageChange(fePage - 1)}
                  handleNextPage={() => fePage < feTotalPages && handleFePageChange(fePage + 1)}
                  handleRowLimitChange={() => {}}
                />
              </div>
            </>
          )}
        </div>
      )}{" "}
      {/* end app_check tab */}
    </div>
  );
};

export default ErrorLogsScreen;
